import React, { createContext, useContext, useMemo } from 'react';

/**
 * How a value on a read surface reaches the list's filter.
 *
 * The read view, the detail pane, the inline mobile detail, a table cell and a
 * card tile all render the same values — but only ONE of them is inside a list
 * that has a `?q=` to narrow. A context is what tells the value whether there
 * is a list behind it: the items page provides `applyValueFilter`, and every
 * surface outside a list (the item route, the public shared page, a modal)
 * provides nothing and the affordance simply does not appear. No prop drilled
 * through five components to be undefined at four of them.
 *
 * `applyValueFilter({ path, forceElement, conditions })` is the one door —
 * see `utils/filterValues.js` for what it does with what it is handed.
 *
 * `facetFor(element)` rides along for the same reason: whether a lookup
 * belongs to a FACET (`@people`) is a fact about the LIST's vocabulary, not
 * about the value — the items page knows it, and a tile five levels down does
 * not. Where it answers, "Filter by Alice" reaches every card that mentions
 * her rather than the one field that was clicked.
 */
const FilterByValueContext = createContext(null);

export function FilterByValueProvider({ applyValueFilter, facetFor = null, children }) {
	const value = useMemo(
		() => (typeof applyValueFilter === 'function' ? { applyValueFilter, facetFor } : null),
		[applyValueFilter, facetFor]
	);
	return (
		<FilterByValueContext.Provider value={value}>
			{children}
		</FilterByValueContext.Provider>
	);
}

/** The apply callback, or null when this surface is not inside a filterable list. */
export function useFilterByValue() {
	return useContext(FilterByValueContext)?.applyValueFilter || null;
}

/** `element → facet field | null`; answers null everywhere no vocabulary was supplied. */
export function useValueFacets() {
	const facetFor = useContext(FilterByValueContext)?.facetFor;
	return typeof facetFor === 'function' ? facetFor : null;
}

export default FilterByValueContext;
